import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/**
 * Converts markdown text to safe HTML for rendering in chat bubbles.
 * HTML-escapes input first (XSS safe), then applies markdown transformations.
 */
@Pipe({ name: 'markdown', standalone: true })
export class MarkdownPipe implements PipeTransform {
  constructor(private sanitizer: DomSanitizer) {}

  transform(value: string): SafeHtml {
    if (!value) return '';

    let html = this.escapeHtml(value);
    html = this.reduceBoldDensity(html);
    html = this.convertMarkdown(html);

    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * If the text has more than 3 bold segments, keep only the first 2.
   * Safety net for when the LLM overuses bold despite prompt instructions.
   */
  private reduceBoldDensity(text: string): string {
    const boldPattern = /\*\*(.+?)\*\*/g;
    const matches = text.match(boldPattern);
    if (!matches || matches.length <= 3) return text;

    let count = 0;
    return text.replace(boldPattern, (match, inner) => {
      count++;
      return count <= 2 ? match : inner;
    });
  }

  private convertMarkdown(text: string): string {
    // Code blocks (fenced) — must come before inline code
    text = text.replace(/```(\w*)\n?([\s\S]*?)```/g,
      '<pre class="mealy-code-block"><code>$2</code></pre>');

    // Inline code
    text = text.replace(/`([^`]+)`/g, '<code class="mealy-inline-code">$1</code>');

    // Bold + italic
    text = text.replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>');

    // Bold
    text = text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');

    // Italic
    text = text.replace(/(?<!\*)\*([^*]+?)\*(?!\*)/g, '<em>$1</em>');

    // Headings (## and ###, skip # — too large for chat)
    text = text.replace(/^### (.+)$/gm, '<div class="mealy-md-h3">$1</div>');
    text = text.replace(/^## (.+)$/gm, '<div class="mealy-md-h2">$1</div>');
    text = text.replace(/^# (.+)$/gm, '<div class="mealy-md-h2">$1</div>');

    // Horizontal rule
    text = text.replace(/^---$/gm, '<hr class="mealy-md-hr">');

    // Markdown links [text](url) — after HTML escape, parens/brackets are intact.
    // An internal route (starts with /) navigates in-app via a delegated click
    // handler (see md-ai-panel/meal-dynamics-ai components) instead of a full
    // reload in a new tab.
    text = text.replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_match, label, url) => {
      if (url.startsWith('/')) {
        return `<a class="mealy-md-link mealy-md-button" data-route="${url}" href="${url}">${label}</a>`;
      }
      return `<a class="mealy-md-link" href="${url}" target="_blank" rel="noopener">${label}</a>`;
    });

    // Unordered lists: convert consecutive `- item` lines into <ul>
    text = text.replace(/(^|\n)(- .+(?:\n- .+)*)/g, (_match, prefix, block) => {
      const items = block.split('\n').map((line: string) => {
        const content = line.replace(/^- /, '');
        return `<li>${content}</li>`;
      }).join('');
      return `${prefix}<ul class="mealy-md-list">${items}</ul>`;
    });

    // Ordered lists: convert consecutive `1. item` lines into <ol>
    text = text.replace(/(^|\n)(\d+\. .+(?:\n\d+\. .+)*)/g, (_match, prefix, block) => {
      const items = block.split('\n').map((line: string) => {
        const content = line.replace(/^\d+\. /, '');
        return `<li>${content}</li>`;
      }).join('');
      return `${prefix}<ol class="mealy-md-list mealy-md-ol">${items}</ol>`;
    });

    // Line breaks: double newline = paragraph break, single = <br>
    text = text.replace(/\n\n/g, '<div class="mealy-md-break"></div>');
    text = text.replace(/\n/g, '<br>');

    return text;
  }
}
